import { Component, Input, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { CustomerService } from '../../../core/services/customer.service';
import { AppUtils } from '../../../core/config/app.utils';
import { LoadingService } from '../../../core/services/loading.service';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { User } from '../../../core/models/user.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Location } from '@angular/common';
import { SETTINGS } from '../../../core/config/common.settings';
import { CountryService } from '../../../core/services/country.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-add-edit-customer',
  templateUrl: './add-edit-customer.component.html',
  styleUrls: ['./add-edit-customer.component.scss'],
})
export class AddEditCustomerComponent implements OnInit {
  @Input() showHeader?: any;
  @Input() isPopUp?: any;
  customerId: any = undefined;
  isAdd = false;
  customerForm: any;
  formErrors: any = {
    first_name: '',
    last_name: '',
    email: '',
    countryCode: '',
    country: '',
    contact: {},
    status: '',
  };
  countries: any[] = [];
  popupButtonLoading = false;
  actionMessage = new ActionAlert();
  singleCustomer: any = {};

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private customerService: CustomerService,
    private loading: LoadingService,
    private msg: NzMessageService,
    private _location: Location,
    private countryService: CountryService,
    @Optional() private modal: NzModalRef,
    private messageService: NzMessageService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.loading.start();
    Promise.all([this.getCountries()]).then(() => this.loading.stop());

    // Set Add or Update Page
    this.customerId = this.route.snapshot.paramMap.get('id');
    this.isAdd = !this.customerId;
    if (!this.isAdd) {
      this.loadCustomerByID();
    }

    this.customerForm = this.fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      email: [
        '',
        [ Validators.email],
      ],
      country: ['', [Validators.required]],
      contact: [
        '',
        [ Validators.pattern(SETTINGS.REGEX.PHONE.EXP)],
      ],
      dialCode: [''],
      active: [true, [Validators.required]],
    });

    this.customerForm.valueChanges.subscribe((form: any) => {
      AppUtils.getFormErrors(this.customerForm, this.formErrors);
    });
  }

  async getCountries(): Promise<void> {
    this.countries = await this.countryService.getCountries();
  }

  async loadCustomerByID(): Promise<any> {
    try {
      this.singleCustomer = await this.customerService.getCustomerByID(
        this.customerId
      );
      this.customerForm.patchValue(this.singleCustomer);
      this.customerForm
        .get('country')
        .setValue(this.singleCustomer?.country?.code);
    } catch (e) {
      this.msg.error(this.translocoService.translate('customers.addEdit.messages.userNotFound'));
      this._location.back();
    }
  }

  async addCustomer(): Promise<any> {
    this.loading.start();
    try {
      const payload = this.customerForm.getRawValue();
      const selected_country = this.countries.find(
        c => c.code === payload.country
      );
      payload.country = {
        name: selected_country.name,
        code: selected_country.code,
        emoji: selected_country.emoji,
      };
      const data = await this.customerService.createCustomer(payload);
      this.customerForm.reset();
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        this.translocoService.translate('customers.addEdit.messages.added'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        e.error?.message || this.translocoService.translate('customers.addEdit.messages.notAdded'),
        'error'
      );
      this.loading.stop();
    }
  }

  async addCustomerPopup(): Promise<void> {
    this.popupButtonLoading = true;
    try {
      const payload = this.customerForm.getRawValue();
      const selected_country = this.countries.find(
        c => c.code === payload.country
      );
      payload.country = {
        name: selected_country.name,
        code: selected_country.code,
        emoji: selected_country.emoji,
      };
      const data = await this.customerService.createCustomer(payload);
      this.popupButtonLoading = false;
      this.customerForm.reset();
      this.messageService.success(this.translocoService.translate('customers.addEdit.messages.added'));
      this.modal.close({message: "success", customer: data._id});
    } catch (e: any) {
      this.popupButtonLoading = false;
      this.messageService.error(e.error?.message || this.translocoService.translate('customers.addEdit.messages.notAdded'));
      this.modal.close({message: "error"});
    }
  }

  async editCustomer(): Promise<any> {
    this.loading.start();
    try {
      const payload = {
        _id: this.customerId,
        ...this.customerForm.getRawValue(),
      };
      const selected_country = this.countries.find(
        c => c.code === payload.country
      );
      payload.country = {
        name: selected_country.name,
        code: selected_country.code,
        emoji: selected_country.emoji,
      };
      const data = await this.customerService.updateCustomer(payload);
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        this.translocoService.translate('customers.addEdit.messages.updated'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        e.error?.message || this.translocoService.translate('customers.addEdit.messages.notUpdated'),
        'error'
      );
      this.loading.stop();
    }
  }
  orderByName(list: any[]): any {
    return AppUtils.sortOrderByName(list);
  }
}
